import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Typography, TextField, Button, CircularProgress, Alert,
    Box, Paper, IconButton, List, ListItem, Divider, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CodeIcon from '@mui/icons-material/Code';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

const API_URL = process.env.REACT_APP_API_URL;

// Helper to convert form state array to the object structure needed by API
const formCategoriesToObject = (formCats) => {
    return formCats.reduce((acc, cat) => {
        // Ensure category name is not empty and subjects is an array
        if (cat.name.trim() && Array.isArray(cat.subjects)) {
            // Filter out empty subjects
            const validSubjects = cat.subjects.map(s => s.trim()).filter(s => s !== '');
            if (validSubjects.length > 0) {
                acc[cat.name.trim()] = validSubjects;
            }
        }
        return acc;
    }, {});
};

// Helper to convert API object structure to form state array
const objectToFormCategories = (apiObject) => {
    if (!apiObject || typeof apiObject !== 'object') {
        return [];
    }
    return Object.entries(apiObject).map(([name, subjects], index) => ({
        id: `cat-${index}-${Date.now()}-${Math.random()}`,
        name: name,
        subjects: Array.isArray(subjects) ? [...subjects] : []
    }));
};

function AdminCategoriesSettings({ userId, businessId, businessName, open, onClose, onSuccess }) {
    const [categoriesJson, setCategoriesJson] = useState('{}');
    const [formCategories, setFormCategories] = useState([]);
    const [initialJson, setInitialJson] = useState('{}');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [showJsonEditor, setShowJsonEditor] = useState(false);

    // Refs for managing focus
    const categoryInputRefs = useRef({});
    const subjectInputRefs = useRef({});

    // Fetch Categories
    const fetchCategories = useCallback(async () => {
        if (!userId || !businessId) {
            setError("Missing user ID or business ID.");
            setFormCategories([]);
            setCategoriesJson('{}');
            setInitialJson('{}');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        setValidationError(null);
        try {
            const url = `${API_URL}/admin/users/${userId}/businesses/${businessId}/categories`;
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ detail: response.statusText }));
                throw new Error(errorData.detail || `Failed to fetch categories (Status: ${response.status})`);
            }

            const data = await response.json();
            const categoriesObject = data || {};

            // Convert to form state and JSON string
            const formState = objectToFormCategories(categoriesObject);
            const jsonString = JSON.stringify(categoriesObject, null, 2);

            setFormCategories(formState);
            setCategoriesJson(jsonString);
            setInitialJson(jsonString);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError(err.message || 'Failed to load categories.');
            setFormCategories([]);
            setCategoriesJson('{}');
            setInitialJson('{}');
        } finally {
            setIsLoading(false);
        }
    }, [userId, businessId]);

    useEffect(() => {
        if (open) {
            fetchCategories();
        }
    }, [open, fetchCategories]);

    // Sync Form State -> JSON State
    useEffect(() => {
        try {
            const newJsonObject = formCategoriesToObject(formCategories);
            const newJsonString = JSON.stringify(newJsonObject, null, 2);
            setCategoriesJson(newJsonString);
            setValidationError(null);
        } catch (e) {
            console.error("Error converting form state to JSON:", e);
            setValidationError("Internal error converting form data to display in JSON editor.");
        }
    }, [formCategories]);

    // Handle JSON Text Field Change
    const handleJsonChange = (event) => {
        const newJsonString = event.target.value;
        setCategoriesJson(newJsonString);
        try {
            const parsed = JSON.parse(newJsonString);
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                throw new Error("Input must be a valid JSON object.");
            }
            setFormCategories(objectToFormCategories(parsed));
            setError(null);
            setValidationError(null);
        } catch (parseError) {
            console.error("JSON Parse Error on manual edit:", parseError);
            setError(`Invalid JSON: ${parseError.message}`);
            setValidationError(null);
        }
    };

    // Save Handler
    const handleSave = async () => {
        let parsedCategories;
        try {
            parsedCategories = JSON.parse(categoriesJson);
            if (typeof parsedCategories !== 'object' || parsedCategories === null || Array.isArray(parsedCategories)) {
                throw new Error("Cannot save invalid format. Must be a JSON object.");
            }
            setError(null);
            setValidationError(null);
        } catch (parseError) {
            console.error("JSON Parse Error on save:", parseError);
            setError(`Invalid JSON: ${parseError.message}`);
            return;
        }

        // Check for empty categories or subjects before saving
        const finalObject = formCategoriesToObject(formCategories);
        if (Object.keys(finalObject).length === 0 && formCategories.length > 0) {
            setValidationError("Cannot save with empty category names or categories with only empty subjects.");
            return;
        }
        if (Object.values(finalObject).some(subjects => subjects.length === 0)) {
            setValidationError("All categories must have at least one non-empty subject.");
            return;
        }

        setIsSaving(true);
        setError(null);
        setValidationError(null);
        setSaveSuccess(false);

        try {
            const url = `${API_URL}/admin/users/${userId}/businesses/${businessId}/categories`;
            const response = await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(finalObject)
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ detail: response.statusText }));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            setSaveSuccess(true);
            setInitialJson(JSON.stringify(finalObject, null, 2));
            setCategoriesJson(JSON.stringify(finalObject, null, 2));
            setFormCategories(objectToFormCategories(finalObject));
            setError(null);
            
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error(`Error saving categories:`, err);
            setError(err.message || 'Failed to save categories.');
        } finally {
            setIsSaving(false);
        }
    };

    // Category Handlers
    const handleAddCategory = () => {
        setFormCategories(prev => [
            ...prev,
            { id: `cat-${Date.now()}-${Math.random()}`, name: 'New Category', subjects: ['New Subject'] }
        ]);
        setValidationError(null);
        setError(null);
    };

    const handleRemoveCategory = (idToRemove) => {
        setFormCategories(prev => prev.filter(cat => cat.id !== idToRemove));
    };

    const handleCategoryNameChange = (idToUpdate, newName) => {
        setFormCategories(prev => prev.map(cat =>
            cat.id === idToUpdate ? { ...cat, name: newName } : cat
        ));
    };

    const handleAddSubject = (categoryId) => {
        setFormCategories(prev => prev.map(cat =>
            cat.id === categoryId ? { ...cat, subjects: [...cat.subjects, 'New Subject'] } : cat
        ));
        setValidationError(null);
        setError(null);
    };

    const handleRemoveSubject = (categoryId, subjectIndexToRemove) => {
        setFormCategories(prev => prev.map(cat =>
            cat.id === categoryId
                ? { ...cat, subjects: cat.subjects.filter((_, index) => index !== subjectIndexToRemove) }
                : cat
        ));
    };

    const handleSubjectNameChange = (categoryId, subjectIndexToUpdate, newSubjectName) => {
        setFormCategories(prev => prev.map(cat =>
            cat.id === categoryId
                ? { ...cat, subjects: cat.subjects.map((subj, index) => index === subjectIndexToUpdate ? newSubjectName : subj) }
                : cat
        ));
    };

    const handleClose = () => {
        setFormCategories([]);
        setCategoriesJson('{}');
        setInitialJson('{}');
        setError(null);
        setValidationError(null);
        setSaveSuccess(false);
        setShowJsonEditor(false);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="lg" 
            fullWidth
            PaperProps={{
                sx: { height: '80vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PlaylistAddCheckIcon sx={{ mr: 1, color: '#1976d2' }} />
                    <Typography variant="h6">
                        Edit Categories - {businessName}
                    </Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: '#1976d2' }} />
                    </Box>
                ) : (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        {validationError && <Alert severity="warning" sx={{ mb: 2 }}>{validationError}</Alert>}
                        {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Categories updated successfully!</Alert>}

                        <Grid container spacing={3} sx={{ flex: 1 }}>
                            <Grid size={{ xs: 12, md: showJsonEditor ? 6 : 12 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ color: '#0A2540' }}>
                                        Categories & Subjects
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CodeIcon />}
                                        onClick={() => setShowJsonEditor(!showJsonEditor)}
                                        sx={{ 
                                            color: '#1976d2',
                                            borderColor: '#1976d2',
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                borderColor: '#1976d2'
                                            }
                                        }}
                                    >
                                        {showJsonEditor ? 'Hide JSON Editor' : 'Show JSON Editor'}
                                    </Button>
                                </Box>

                                <Paper sx={{
                                    p: 1.5, 
                                    background: 'rgba(255,255,255,0.8)', 
                                    height: '400px',
                                    overflowY: 'auto',
                                    flex: 1
                                }} elevation={0} variant="outlined">
                                    <List dense disablePadding>
                                        {formCategories.map((category, catIndex) => (
                                            <Paper key={category.id} variant="outlined" sx={{ mb: 2, p: 1.5, background: 'rgba(255,255,255,0.6)' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        variant="standard"
                                                        size="small"
                                                        value={category.name}
                                                        onChange={(e) => handleCategoryNameChange(category.id, e.target.value)}
                                                        placeholder={`Category ${catIndex + 1} Name`}
                                                        sx={{ mr: 1, '.MuiInput-input': { fontWeight: 'bold' } }}
                                                        error={!category.name.trim()}
                                                    />
                                                    <IconButton
                                                        onClick={() => handleRemoveCategory(category.id)}
                                                        size="small"
                                                        color="error"
                                                    >
                                                        <RemoveCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                
                                                <List dense sx={{ pl: 2 }}>
                                                    {category.subjects.map((subject, subjIndex) => (
                                                        <ListItem key={subjIndex} disableGutters>
                                                            <TextField
                                                                fullWidth
                                                                variant="standard"
                                                                size="small"
                                                                value={subject}
                                                                onChange={(e) => handleSubjectNameChange(category.id, subjIndex, e.target.value)}
                                                                placeholder={`Subject ${subjIndex + 1}`}
                                                                sx={{ mr: 1 }}
                                                                error={!subject.trim()}
                                                            />
                                                            <IconButton
                                                                onClick={() => handleRemoveSubject(category.id, subjIndex)}
                                                                size="small"
                                                                color="error"
                                                            >
                                                                <RemoveCircleOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </ListItem>
                                                    ))}
                                                    <ListItem disableGutters>
                                                        <TextField
                                                            fullWidth
                                                            variant="standard"
                                                            size="small"
                                                            placeholder="Add New Subject... (Press Enter)"
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && e.target.value.trim() !== '') {
                                                                    e.preventDefault();
                                                                    handleAddSubject(category.id);
                                                                    e.target.value = '';
                                                                }
                                                            }}
                                                            sx={{ mr: 1, '.MuiInput-input': { fontStyle: 'italic' } }}
                                                        />
                                                    </ListItem>
                                                </List>
                                            </Paper>
                                        ))}
                                        
                                        <Paper variant="outlined" sx={{ mb: 2, p: 1.5, background: 'rgba(245,245,255,0.4)', borderStyle: 'dashed' }}>
                                            <TextField
                                                fullWidth
                                                variant="standard"
                                                size="small"
                                                placeholder="Add New Category... (Press Enter)"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.target.value.trim() !== '') {
                                                        e.preventDefault();
                                                        const newCategoryName = e.target.value.trim();
                                                        const newCategoryId = `cat-${formCategories.length}-${Date.now()}-${Math.random()}`;
                                                        setFormCategories(prev => [
                                                            ...prev,
                                                            { id: newCategoryId, name: newCategoryName, subjects: [] }
                                                        ]);
                                                        e.target.value = '';
                                                    }
                                                }}
                                                sx={{ '.MuiInput-input': { fontStyle: 'italic' } }}
                                            />
                                        </Paper>
                                    </List>
                                </Paper>
                            </Grid>

                            {showJsonEditor && (
                                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#0A2540', pt: '4px' }}>
                                        Edit as JSON
                                    </Typography>
                                    <TextField
                                        label="Custom Categories (JSON)"
                                        multiline
                                        value={categoriesJson}
                                        onChange={handleJsonChange}
                                        variant="outlined"
                                        fullWidth
                                        error={!!error && error.startsWith('Invalid JSON')}
                                        sx={{ 
                                            fontFamily: 'monospace', 
                                            flexGrow: 1, 
                                            '& .MuiInputBase-root': { height: '100%' }, 
                                            '& .MuiInputBase-inputMultiline': { height: '100% !important', overflowY: 'auto !important' },
                                        }}
                                        inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} color="secondary">
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave}
                    variant="contained"
                    disabled={isSaving || isLoading}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit"/> : <PlaylistAddCheckIcon />}
                >
                    {isSaving ? 'Saving...' : 'Save Categories'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AdminCategoriesSettings; 